<script setup>
import { computed, inject } from 'vue';
import { accentParts, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// The cards and the button are what this block is for; a heading with
// neither under it is a heading over nothing, and a visitor gets no
// block at all. The editor keeps it and its prompt (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const cards = computed(() => (Array.isArray(props.config.cards) ? props.config.cards : []).filter((c) => editing || c?.name || c?.icon || c?.detail));
const hasContent = computed(() => cards.value.length > 0 || Boolean(String(props.config.cta_label || '').trim()));
const headingParts = computed(() => accentParts(props.config.heading));
const hasInlineAccent = computed(() => headingParts.value.some((p) => p.accent));
</script>

<template>
    <section v-if="hasContent || editing" :id="config.anchor_id || null" class="th-block th-band th-dark th-ft" :style="styleVars(config)">
        <div class="th-wrap th-ft__inner">
            <h2 v-if="config.heading || config.heading_accent" class="th-h2 th-ft__heading">
                <template v-for="(part, i) in headingParts" :key="i">
                    <span v-if="part.accent" class="th-accent-word">{{ part.text }}</span>
                    <template v-else>{{ part.text }}</template>
                </template>
                <template v-if="!hasInlineAccent && config.heading_accent">
                    {{ ' ' }}<span class="th-accent-word">{{ config.heading_accent }}</span>
                </template>
            </h2>
            <p v-if="config.subtitle" class="th-lede th-ft__lede">{{ config.subtitle }}</p>

            <div v-if="cards.length" class="th-ft__grid">
                <component v-for="(card, i) in cards" :key="i"
                           :is="card.href ? 'a' : 'div'"
                           :href="card.href || null"
                           class="th-ft__card">
                    <div v-if="card.icon" class="th-ft__icon" aria-hidden="true">{{ card.icon }}</div>
                    <div class="th-ft__name">{{ card.name }}</div>
                    <div v-if="card.detail" class="th-ft__detail">{{ card.detail }}</div>
                </component>
            </div>
            <p v-else-if="editing" class="th-small th-ft__empty">Add trail cards in the editor.</p>

            <a v-if="config.cta_label" :href="config.cta_href || '#'" class="th-btn th-btn--primary th-ft__cta">
                {{ config.cta_label }}
            </a>
        </div>
    </section>
</template>

<style scoped>
.th-ft__inner { text-align: center; }
.th-ft__heading { font-size: clamp(34px, 5.5vw, 68px); }
.th-ft__lede { margin: 16px auto 0; }
.th-ft__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
    gap: 12px;
    margin-top: clamp(28px, 4vw, 44px);
    text-align: left;
}
.th-ft__card {
    background: rgba(255, 255, 255, .05);
    border: 1px solid rgba(255, 255, 255, .13);
    border-radius: var(--r);
    padding: 22px 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-decoration: none !important;
    transition: background-color .15s ease, border-color .15s ease;
}
a.th-ft__card:hover { background: rgba(255, 255, 255, .1); border-color: var(--accent); }
.th-ft__icon { font-size: 28px; line-height: 1; margin-bottom: 4px; }
.th-ft__name { font: 700 19px/1.2 var(--th-display); }
.th-ft__detail { font-size: 15px; color: var(--text-sub, #c8ccc5); }
.th-ft__empty { margin-top: 24px; font-style: italic; color: var(--text-sub, #c8ccc5); }
.th-ft__cta { margin-top: clamp(28px, 4vw, 44px); }
</style>
